import { Nullable } from '../../types/common'
import { isMobile } from '../../utils/check-media'
import { SidebarComponent } from '../../components/sidebar/sidebar'
import { $el, $elParent } from 'src/scripts/utils/utils'

const SEARCH_INPUT_SELECTOR = `[data-sidebar-search-input]`
const CONTENT_SELECTOR = `[data-sidebar-search-content]`
const CONTENT_RESULTS_SELECTOR = `[data-sidebar-search-content-results]`

export class SearchSidebar extends SidebarComponent {
  searchInput: Nullable<HTMLInputElement>
  resizeObserver: ResizeObserver
  isInputFocused: boolean
  defaultStateElement: Nullable<HTMLElement>

  constructor() {
    super()
    this.searchInput = $el(SEARCH_INPUT_SELECTOR, this.element)
    this.defaultStateElement = this.element.cloneNode(true) as HTMLElement
  }

  mountComponent(): void {
    super.mountComponent()

    const content = $el(CONTENT_SELECTOR, this.element)
    const contentResults = $el(CONTENT_RESULTS_SELECTOR, this.element)

    if (contentResults) {
      this.resizeObserver = new ResizeObserver(this.handleContentResultsResize)
      this.resizeObserver.observe(contentResults)
    }

    this.addListener(content, 'scroll', this.handleContentScroll)
    this.addListener(this, 'click', this.handleClick)
    this.addListener(this, 'touchend', this.handleSearchSidebarTouchEnd)
    this.addListener(this.searchInput, 'focus', this.handleInputFocus)
  }

  unmountComponent(): void {
    this.resizeObserver.disconnect()
    super.unmountComponent()
  }

  handleClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement
    const isInput = target === this.searchInput
    const isLink = target.tagName === 'A'
    const queryElement = $elParent('[data-sidebar-search-query]', target)

    if (this.searchInput && queryElement) {
      const event = new Event('input', { bubbles: true })

      this.searchInput.value = `${queryElement.getAttribute(
        'data-sidebar-search-query',
      )}`
      this.searchInput.dispatchEvent(event)
      this.searchInput.focus()
    }

    if (isMobile() && isInput) {
      this.isInputFocused = true
    }

    if (!isInput && this.isInputFocused) {
      if (isLink) {
        event.preventDefault()
      }

      this.isInputFocused = false
    }
  }

  handleInputFocus = (): void => {
    if (isMobile()) {
      this.isInputFocused = true
    }
  }

  handleSearchSidebarTouchEnd = (event: TouchEvent): void => {
    const THRESHOLD = 12
    const target = event.target as HTMLElement
    const distanceX = Math.abs(this.touchStartX - this.touchEndX)
    const distanceY = Math.abs(this.touchStartY - this.touchEndY)
    const isOverThreshold = distanceX > THRESHOLD || distanceY > THRESHOLD

    if (this.isInputFocused && isOverThreshold && this.searchInput) {
      if (target.tagName === 'A') {
        event.preventDefault()
      }

      this.searchInput.blur()
      this.isInputFocused = false
    }

    this.cleanTouchPoints()
  }

  handleContentResultsResize = (): void => {
    this.toggleAttribute('footer-shadow-visible', this.isContentOverlow())
  }

  handleContentScroll = (): void => {
    const content = $el(CONTENT_SELECTOR, this.element)

    if (content) {
      this.toggleAttribute(
        'header-shadow-visible',
        this.isContentOverlow() && content.scrollTop > 0,
      )
    }
  }

  async open(event): Promise<void> {
    super.open(event)

    if (this.searchInput && !this.isEditor) {
      this.searchInput.focus()
    }
  }

  isContentOverlow(): boolean {
    const content = $el(CONTENT_SELECTOR, this.element)
    const contentResults = $el(CONTENT_RESULTS_SELECTOR, this.element)

    if (!contentResults || !content) {
      return false
    }

    return contentResults.offsetHeight > content.offsetHeight
  }
}
